/**
 * This file includes polyfills needed by Angular and is loaded before the app.
 * You can add your own extra polyfills to this file.
 *
 * This file is divided into 2 sections:
 *   1. Browser polyfills. These are applied before loading ZoneJS and are sorted by browsers.
 *   2. Application imports. Files imported after ZoneJS that should be loaded before your main
 *      file.
 *
 * The current setup is for so-called "evergreen" browsers; the last versions of browsers that
 * automatically update themselves. This includes recent versions of Safari, Chrome (including
 * Opera), Edge on the desktop, and iOS and Chrome on mobile.
 *
 * Learn more in https://angular.io/guide/browser-support
 */

/***************************************************************************************************
 * BROWSER POLYFILLS
 */
declare global {
    interface String {
        toLowerCamelCase(): string;

        toTitleCase(): string;

        /**
         * This function converts a string to a case similar to a sentence.
         * Example:
         * - source: "THIS IS A TEXT" or "this is a text" or "tHiS iS a TeXt"
         * - result: "This is a string"
         */
        toSentenceCase(): string;

        capitalizeFirstLetter(): string;

        splitByCapital(): string;

        replaceAll(search: string | RegExp, replacement: string): string;

        toUpperSnakeCase(): string;
    }

    interface Array<T> {
        /**
         * This function sorts an array of objects based on the given property
         * @param {string} property - the property key by which to sort
         * @param {boolean} [ascending = true] - true means ascending, false means descending
         * @param {boolean} [caseSensitive = false]
         */
        orderBy(property: string, ascending?: boolean, caseSensitive?: boolean): Array<T>;

        findIndexFromEnd(comparator: (item: string, index: number) => unknown): number;
    }

    interface StringConstructor {
        EMPTY: string;
    }
}

if (!Array.prototype.orderBy) {
    Object.defineProperty(Array.prototype, 'orderBy', {
        value: function (property: string, ascending: boolean = true, caseSensitive: boolean = false): [] {
            const direction = ascending ? 1 : -1;
            const sortPreprocessor = caseSensitive
                ? (input: any) => input
                : (input: any) => (typeof input === 'string' ? input.toLowerCase() : input);
            return this.sort(function (a: any, b: any) {
                if (sortPreprocessor(a[property]) < sortPreprocessor(b[property])) {
                    return -1 * direction;
                } else if (sortPreprocessor(a[property]) > sortPreprocessor(b[property])) {
                    return 1 * direction;
                } else {
                    return 0;
                }
            });
        },
    });
}

if (!Array.prototype.findIndexFromEnd) {
    Object.defineProperty(Array.prototype, 'findIndexFromEnd', {
        value: function (comparator: Function): number {
            let index = -1;

            for (let i = this.length; i >= 0; i--) {
                const item = this[i];

                if (comparator(item, i)) {
                    return i;
                }
            }

            return index;
        },
    });
}

String.EMPTY = '';

// polyfill String.prototype.toTitleCase()
if (!String.prototype.toTitleCase) {
    Object.defineProperty(String.prototype, 'toTitleCase', {
        value: function (): string {
            return this.replace(/\w\S*/g, function (txt: string) {
                return txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase();
            });
        },
    });
}

// polyfill String.prototype.toLowerCamelCase()
if (!String.prototype.toLowerCamelCase) {
    Object.defineProperty(String.prototype, 'toLowerCamelCase', {
        value: function (): string {
            return this.replaceAll('-', ' ')
                .replaceAll('_', ' ')
                .toTitleCase()
                .replaceAll(' ', '')
                .replace(/\w\S*/g, function (txt: string) {
                    return txt.charAt(0).toLowerCase() + txt.substring(1);
                });
        },
    });
}

// polyfill String.prototype.capitalizeFirstWord()
if (!String.prototype.toSentenceCase) {
    Object.defineProperty(String.prototype, 'toSentenceCase', {
        value: function (): string {
            return this.charAt(0).toUpperCase() + this.substring(1).toLowerCase();
        },
    });
}

// polyfill String.prototype.capitalizeFirstLetter()
if (!String.prototype.capitalizeFirstLetter) {
    Object.defineProperty(String.prototype, 'capitalizeFirstLetter', {
        value: function (): string {
            return this.charAt(0).toUpperCase() + this.substring(1);
        },
    });
}

// polyfill String.prototype.splitByCapital()
if (!String.prototype.splitByCapital) {
    Object.defineProperty(String.prototype, 'splitByCapital', {
        value: function (): string {
            return this.replace(/([A-Z])/g, ' $1');
        },
    });
}

// polyfill String.prototype.replaceAll()
if (!String.prototype.replaceAll) {
    Object.defineProperty(String.prototype, 'replaceAll', {
        value: function (search: string | RegExp, replacement: string): string {
            let target = this;

            if (search instanceof RegExp) return target.replace(search, replacement);

            return target.replace(new RegExp(search, 'g'), replacement);
        },
    });
}

// Polyfill for String.prototype.toUpperSnakeCase
if (!String.prototype.toUpperSnakeCase) {
    Object.defineProperty(String.prototype, 'toUpperSnakeCase', {
        value: function (): string {
            // Step 1: Insert underscores before uppercase letters that follow lowercase letters or digits
            const snakeCase = this.replace(/([a-z0-9])([A-Z])/g, '$1_$2');

            // Step 2: Convert the entire string to uppercase
            return snakeCase.toUpperCase();
        },
        writable: true, // Allow the property to be writable
        configurable: true, // Allow the property to be configurable
    });
}

/**
 * By default, zone.js will patch all possible macroTask and DomEvents
 * users can disable parts of macroTask/DomEvents patch by setting following flags
 * because those flags need to be set before `zone.js` being loaded, and webpack
 * will put import in the top of bundle, so users need to create a separate file
 * in this directory (for example: zone-flags.ts), and put the following flags
 * into that file, and then add the following code before importing zone.js.
 * import './zone-flags';
 *
 * The flags allowed in zone-flags.ts are listed here.
 *
 * The following flags will work for all browsers.
 *
 * (window as any).__Zone_disable_requestAnimationFrame = true; // disable patch requestAnimationFrame
 * (window as any).__Zone_disable_on_property = true; // disable patch onProperty such as onclick
 * (window as any).__zone_symbol__UNPATCHED_EVENTS = ['scroll', 'mousemove']; // disable patch specified eventNames
 *
 *  in IE/Edge developer tools, the addEventListener will also be wrapped by zone.js
 *  with the following flag, it will bypass `zone.js` patch for IE/Edge
 *
 *  (window as any).__Zone_enable_cross_context_check = true;
 *
 */

/**
 * By default, zone.js will patch all possible macroTask and DomEvents
 * users can disable parts of macroTask/DomEvents patch by setting following flags
 * because those flags need to be set before `zone.js` being loaded, and webpack
 * will put import in the top of bundle, so users need to create a separate file
 * in this directory (for example: zone-flags.ts), and put the following flags
 * into that file, and then add the following code before importing zone.js.
 * import './zone-flags';
 *
 * The flags allowed in zone-flags.ts are listed here.
 *
 * The following flags will work for all browsers.
 *
 * (window as any).__Zone_disable_requestAnimationFrame = true; // disable patch requestAnimationFrame
 * (window as any).__Zone_disable_on_property = true; // disable patch onProperty such as onclick
 * (window as any).__zone_symbol__UNPATCHED_EVENTS = ['scroll', 'mousemove']; // disable patch specified eventNames
 *
 *  in IE/Edge developer tools, the addEventListener will also be wrapped by zone.js
 *  with the following flag, it will bypass `zone.js` patch for IE/Edge
 *
 *  (window as any).__Zone_enable_cross_context_check = true;
 *
 */
/***************************************************************************************************
 * Zone JS is required by default for Angular itself.
 */
import 'zone.js'; // Included with Angular CLI.

/***************************************************************************************************
 * APPLICATION IMPORTS
 */
